import { useState } from 'react'
import { X } from 'lucide-react'
import { type AudioGroup, type AudioTrack } from '../../../api'
import { Button, ChipSelect, IconButton, Modal, ModalActions, Pill } from '../../../components/ui'
import styles from './AudioBindDialog.module.css'

type AudioBindDialogProps = {
  groups: AudioGroup[]
  track: AudioTrack
  onClose: () => void
  onSave: (audioGroupIds: string[]) => Promise<void>
}

export const AudioBindDialog = (props: AudioBindDialogProps) => {
  const [groupIds, setGroupIds] = useState<string[]>(
    props.track.groups.map((group) => group.id)
  )
  const [saving, setSaving] = useState(false)
  const cssClass = (...classNames: Array<string | false | null | undefined>): string =>
    classNames.filter(Boolean).join(' ')
  const toggleGroup = (groupId: string): void => {
    setGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    )
  }

  return (
    <Modal
      ariaLabelledBy="bind-audio-title"
      backdropClassName={cssClass(styles.backdrop)}
      onClose={props.onClose}
      panelClassName={cssClass(styles.panel)}
    >
        <div className={styles.heading}>
          <div className={styles.headingCopy}>
            <h2 id="bind-audio-title">Manage groups - {props.track.title}</h2>
            <p>Select audio groups for this track. A track can belong to multiple groups.</p>
          </div>
          <div className={styles.headingActions}>
            <Pill strong>{groupIds.length} selected</Pill>
            <IconButton
              className="modal-close-button"
              icon={<X size={18} />}
              label="Close group binding"
              onClick={props.onClose}
            />
          </div>
        </div>
        <ChipSelect
          className={styles.chipSelect}
          empty={<span className="muted">No audio groups created.</span>}
          onToggle={toggleGroup}
          options={props.groups.map((group) => ({ id: group.id, label: group.name }))}
          selectedIds={groupIds}
        />
        <ModalActions>
          <Button onClick={props.onClose} variant="secondary">Cancel</Button>
          <Button
            disabled={saving}
            onClick={() => {
              setSaving(true)
              void props.onSave(groupIds).finally(() => setSaving(false))
            }}
            variant="primary"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ModalActions>
    </Modal>
  )
}
